/**
 * Push Notification client helpers (customer web)
 *
 * Wraps Web Push API calls with timeouts so the UI never hangs
 * indefinitely on iOS PWA / slow APNs / broken Service Worker state.
 */

const DEFAULT_SW_READY_MS = 15_000;
const DEFAULT_SUBSCRIBE_MS = 10_000;

export class PushTimeoutError extends Error {
  constructor(label: string, ms: number) {
    super(`[push] "${label}" timed out after ${ms}ms`);
    this.name = "PushTimeoutError";
  }
}

export function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => reject(new PushTimeoutError(label, ms)), ms);
    promise.then(
      (value) => {
        clearTimeout(id);
        resolve(value);
      },
      (err) => {
        clearTimeout(id);
        reject(err);
      }
    );
  });
}

/**
 * Detect if the page is running as an installed PWA (standalone display
 * mode). iOS Safari CANNOT receive Web Push unless the user has added
 * the site to their home screen, so we surface this to the UI.
 */
export function isStandalonePwa(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS legacy flag (still true on iOS 18 when opened from home screen)
    // deno-lint-ignore no-explicit-any
    (navigator as any).standalone === true
  );
}

export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export function isAndroid(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android/.test(navigator.userAgent);
}

export function isMobile(): boolean {
  return isIOS() || isAndroid();
}

/**
 * iOS requires the site to be installed to home screen before push works.
 * Returns true when we should block the "Enable" flow and show the install guide.
 */
export function needsIOSInstallGuide(): boolean {
  return isIOS() && !isStandalonePwa();
}

/**
 * Returns true when we should show a PWA install guide.
 * iOS: required for push. Android: recommended for better UX.
 */
export function needsMobileInstallGuide(): boolean {
  return isMobile() && !isStandalonePwa();
}

export type MobilePlatform = "ios" | "android";

/**
 * Convert a URL-safe base64 VAPID public key to Uint8Array.
 * iOS Safari strictly requires BufferSource for applicationServerKey.
 */
export function urlBase64ToUint8Array(
  base64String: string
): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function getReadyServiceWorker(
  timeoutMs = DEFAULT_SW_READY_MS
): Promise<ServiceWorkerRegistration> {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service Worker not supported in this browser");
  }
  try {
    const existing = await navigator.serviceWorker.getRegistration("/");
    if (!existing) {
      await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
        updateViaCache: "none",
      });
    }
  } catch (err) {
    console.warn("[push] explicit SW register failed:", err);
  }
  return withTimeout(
    navigator.serviceWorker.ready,
    timeoutMs,
    "serviceWorker.ready"
  );
}

export async function getCurrentSubscription(
  timeoutMs = DEFAULT_SW_READY_MS
): Promise<PushSubscription | null> {
  const registration = await getReadyServiceWorker(timeoutMs);
  return withTimeout(
    registration.pushManager.getSubscription(),
    timeoutMs,
    "pushManager.getSubscription"
  );
}

export async function ensurePushSubscription(
  vapidPublicKey: string,
  options: {
    swReadyTimeoutMs?: number;
    subscribeTimeoutMs?: number;
    forceRefresh?: boolean;
  } = {}
): Promise<PushSubscription> {
  const {
    swReadyTimeoutMs = DEFAULT_SW_READY_MS,
    subscribeTimeoutMs = DEFAULT_SUBSCRIBE_MS,
    forceRefresh = false,
  } = options;

  const registration = await getReadyServiceWorker(swReadyTimeoutMs);
  const existing = await withTimeout(
    registration.pushManager.getSubscription(),
    swReadyTimeoutMs,
    "pushManager.getSubscription"
  );

  if (existing && !forceRefresh) {
    return existing;
  }

  if (existing && forceRefresh) {
    try {
      await withTimeout(
        existing.unsubscribe(),
        swReadyTimeoutMs,
        "subscription.unsubscribe"
      );
    } catch (err) {
      console.warn("[push] unsubscribe failed, continuing anyway:", err);
    }
  }

  return withTimeout(
    registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    }),
    subscribeTimeoutMs,
    "pushManager.subscribe"
  );
}

export function encodeSubscriptionKeys(sub: PushSubscription): {
  p256dh: string;
  auth: string;
} {
  const p256dhKey = sub.getKey("p256dh");
  const authKey = sub.getKey("auth");
  if (!p256dhKey || !authKey) {
    throw new Error("Push subscription is missing required keys");
  }
  return {
    p256dh: btoa(String.fromCharCode(...new Uint8Array(p256dhKey))),
    auth: btoa(String.fromCharCode(...new Uint8Array(authKey))),
  };
}

export function isPushTimeoutError(err: unknown): err is PushTimeoutError {
  return err instanceof PushTimeoutError;
}

export { DEFAULT_SW_READY_MS, DEFAULT_SUBSCRIBE_MS };
